import { useState } from "react";
import { Trash2, Camera } from "lucide-react";
import PageHero from "@/components/bakery/PageHero";
import { BinArt } from "@/components/bakery/Art";
import WasteForm from "@/components/forms/WasteForm";
import TxnDetailDialog from "@/components/common/TxnDetailDialog";
import Lightbox from "@/components/common/Lightbox";
import { VoidBadge, Tag } from "@/components/common/Chips";
import { EmptyState, Loading, PrimaryBtn } from "@/components/common/ui";
import { inputCls } from "@/components/common/Field";
import { useApi } from "@/lib/useApi";
import { fileUrl } from "@/lib/api";
import { fmtDate, fmtDateTime, shiftDate, todayJkt } from "@/lib/format";
import { cn } from "@/lib/utils";

function WasteCard({ t, onOpen, onPhoto }) {
  const l = t.lines[0] || {};
  const reason = t.reason === "Lainnya" ? `Lainnya: ${t.reason_other}` : t.reason;
  return (
    <div className={cn("lift overflow-hidden rounded-xl border-2 border-ink bg-white", t.status === "void" && "opacity-60")} data-testid={`waste-card-${t.txn_no}`}>
      <div className="grid gap-0.5 bg-ink" style={{ gridTemplateColumns: `repeat(${Math.min(t.photos?.length || 1, 3)}, 1fr)` }}>
        {(t.photos || []).slice(0, 3).map((p, i) => (
          <button key={p.id} onClick={() => onPhoto(t.photos, i)} className="relative h-40 overflow-hidden bg-zinc-800" data-testid={`waste-thumb-${t.txn_no}-${i}`}>
            <img src={fileUrl(p.id)} alt="bukti waste" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
            {i === 2 && t.photos.length > 3 && <span className="absolute inset-0 grid place-items-center bg-ink/60 text-sm font-bold text-white">+{t.photos.length - 3}</span>}
          </button>
        ))}
      </div>
      <button onClick={() => onOpen(t.id)} className="w-full p-4 text-left" data-testid={`waste-open-${t.txn_no}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate font-semibold">{l.product_name}</div>
            <div className="num text-[11px] text-zinc-400">{t.txn_no} · {fmtDate(t.date)}</div>
          </div>
          <div className="num text-2xl font-extrabold text-brand">−{l.qty}</div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Tag tone="soft">{reason}</Tag><VoidBadge txn={t} />
          <span className="flex items-center gap-1 text-[11px] text-zinc-500"><Camera className="h-3 w-3" /> {t.photos?.length} · PIC {t.pic_name}</span>
        </div>
        <div className="mt-1 text-[10px] text-zinc-400">Dicatat {fmtDateTime(t.created_at)} oleh {t.created_by_name}</div>
      </button>
    </div>
  );
}

export default function TongSampah() {
  const [from, setFrom] = useState(shiftDate(todayJkt(), -6));
  const [to, setTo] = useState(todayJkt());
  const { data: txns, loading, reload } = useApi("/transactions", { type: "WST", date_from: from, date_to: to });
  const { data: products, reload: reloadP } = useApi("/products");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [lb, setLb] = useState({ photos: [], index: null });
  const total = (txns || []).filter((t) => t.status === "active").reduce((s, t) => s + (t.lines[0]?.qty || 0), 0);
  return (
    <div data-testid="waste-page">
      <PageHero testid="waste-hero" eyebrow="Transaksi · Waste Goods" title="Tong Sampah" Art={BinArt}
        desc="Roti basi, gosong, jatuh, berjamur — semuanya dicatat dengan foto bukti. Hanya Admin yang bisa void."
        actions={<PrimaryBtn onClick={() => setOpen(true)} data-testid="open-waste-form"><Trash2 className="h-4 w-4" /> Catat Waste</PrimaryBtn>} />
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={cn(inputCls, "sm:w-48")} data-testid="waste-filter-from" />
        <span className="hidden text-zinc-400 sm:block">—</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={cn(inputCls, "sm:w-48")} data-testid="waste-filter-to" />
        <div className="text-sm text-zinc-600 sm:ml-auto">Total waste periode ini: <span className="num font-bold text-brand" data-testid="waste-total">{total}</span></div>
      </div>
      {loading && !txns ? <Loading /> : txns?.length ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="waste-list">
          {txns.map((t) => <WasteCard key={t.id} t={t} onOpen={setDetail} onPhoto={(photos, index) => setLb({ photos, index })} />)}
        </div>
      ) : <EmptyState Art={BinArt} title="Tong sampah bersih" desc="Tidak ada waste di periode ini." testid="waste-empty" />}
      <WasteForm open={open} onClose={() => setOpen(false)} products={products} defaultDate={todayJkt()} onSaved={() => { setOpen(false); reload(); reloadP(); }} />
      <TxnDetailDialog txnId={detail} onClose={() => setDetail(null)} onChanged={reload} />
      <Lightbox photos={lb.photos} index={lb.index} onIndex={(i) => setLb({ ...lb, index: i })} onClose={() => setLb({ photos: [], index: null })} />
    </div>
  );
}
